import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Truck, ArrowLeft, Pencil, Power, FileStack, Loader2, Upload, FileDown, Play, Eye, X, AlertTriangle, CheckCircle2, GitCompare, Check, Ban, MinusCircle, PackageCheck, Undo2, History, Rocket, Calculator, ClipboardList, Search, ChevronDown, RefreshCw, Database } from "lucide-react";
import { listSuppliers, createSupplier, updateSupplier, supplierDetail, createPriceList,
  uploadPriceListFile, analyzePriceList, priceListRows, priceListFileUrl, newListVersion, autoUpdatePriceList, restorePriceList, nwgReplaceList, nwgRestoreList, legacyBackfill,
  buildDiff, getDiff, diffItemDecision, diffBulk,
  applyPreview, applyDiff, listApplyBatches, rollbackBatch, supplierDiffs, recalcPrices,
  searchListItems, getApplyBatch, rollbackItem, compareListItems } from "@/lib/store";

const LIST_TYPES = ["RICAMBI", "TELEFONI", "ACCESSORI", "PROMOZIONALE"];
const SUPPLIER_LISTS = {
  newwavegames: [{ key: "DISPLAY_TOUCH", label: "Display / Touch" }, { key: "BACK_BATT", label: "Back Cover / Batterie" }],
  erregame: [{ key: "TELEFONIA", label: "Telefonia / Tablet / Accessori" }],
};
const SOURCE_TYPES = ["PDF", "XLS", "CSV", "API", "FEED", "WEB", "ALTRO"];
const ACCEPT = ".pdf,.xls,.xlsx,.csv";
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");
const fmtEur = (c) => (c === null || c === undefined ? "—" : `${(c / 100).toFixed(2).replace(".", ",")} €`);

const IMPORT_BADGE = {
  UPLOADED: "bg-sky-500/15 text-sky-400",
  ANALYZING: "bg-amber-500/15 text-amber-400",
  PARSED: "bg-indigo-500/15 text-indigo-400",
  READY_FOR_REVIEW: "bg-emerald-500/15 text-emerald-400",
  ERROR: "bg-rose-500/15 text-rose-400",
};

const fileToB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(String(r.result).split(",").pop());
  r.onerror = rej;
  r.readAsDataURL(file);
});

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d[0]?.msg || null;
  return null;
};

function SupplierModal({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", code: "", website: "", contact_email: "", phone: "", category: "", source_type: "PDF", note: "", ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name?.trim()) return toast.error("Nome fornitore obbligatorio");
    setBusy(true);
    try {
      if (initial?.id) await updateSupplier(initial.id, f); else await createSupplier(f);
      toast.success("Fornitore salvato"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  const F = ({ k, label, type = "text" }) => (
    <div>
      <label className="mb-1 block text-xs text-zinc-400">{label}</label>
      <input data-testid={`supplier-${k}`} type={type} value={f[k] || ""} onChange={(e) => set(k, e.target.value)}
        className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm text-white outline-none" />
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#11141A] p-5 text-white" data-testid="supplier-modal">
        <h3 className="mb-4 text-lg font-bold">{initial?.id ? "Modifica fornitore" : "Nuovo fornitore"}</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <F k="name" label="Nome fornitore *" />
          <F k="code" label="Codice interno" />
          <F k="website" label="Sito web" />
          <F k="contact_email" label="Email" />
          <F k="phone" label="Telefono" />
          <F k="category" label="Categoria" />
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Tipo fonte</label>
            <select data-testid="supplier-source_type" value={f.source_type || "PDF"} onChange={(e) => set("source_type", e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm text-white outline-none">
              {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-3">
          <label className="mb-1 block text-xs text-zinc-400">Note</label>
          <textarea data-testid="supplier-note" rows={2} value={f.note || ""} onChange={(e) => set("note", e.target.value)}
            className="w-full resize-none rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm text-white outline-none" />
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-white/10 px-4 py-2 text-sm text-zinc-300">Annulla</button>
          <button data-testid="supplier-save" onClick={save} disabled={busy} className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}

function PriceListModal({ supplierId, onClose, onSaved }) {
  const [f, setF] = useState({ name: "", list_type: "RICAMBI", list_date: "", version: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.name?.trim()) return toast.error("Nome/versione obbligatorio");
    setBusy(true);
    try {
      await createPriceList(supplierId, { ...f, list_date: f.list_date ? new Date(f.list_date).toISOString() : null });
      toast.success("Versione listino creata"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Creazione non riuscita"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-white/10 bg-[#11141A] p-5 text-white" data-testid="pricelist-modal">
        <h3 className="mb-4 text-lg font-bold">Nuova versione listino</h3>
        <p className="mb-3 text-xs text-zinc-500">Metadati/versione. Dopo la creazione potrai caricare il file (PDF/XLS/XLSX/CSV) e analizzarlo.</p>
        <label className="mb-1 block text-xs text-zinc-400">Nome / versione *</label>
        <input data-testid="pricelist-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Es. Listino Ricambi 22/09/2026"
          className="mb-3 w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Tipo listino</label>
            <select data-testid="pricelist-type" value={f.list_type} onChange={(e) => set("list_type", e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none">
              {LIST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-zinc-400">Data listino</label>
            <input data-testid="pricelist-date" type="date" value={f.list_date} onChange={(e) => set("list_date", e.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
          </div>
        </div>
        <label className="mb-1 block text-xs text-zinc-400">Note</label>
        <textarea data-testid="pricelist-notes" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)}
          className="mb-4 w-full resize-none rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-white/10 px-4 py-2 text-sm text-zinc-300">Annulla</button>
          <button data-testid="pricelist-save" onClick={save} disabled={busy} className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Crea
          </button>
        </div>
      </div>
    </div>
  );
}

function RowsModal({ lid, name, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { priceListRows(lid, { limit: 500 }).then(setData).catch(() => setData({ rows: [], total: 0 })); }, [lid]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-white/10 bg-[#11141A] text-white" data-testid="rows-modal">
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div>
            <h3 className="text-lg font-bold">Righe estratte</h3>
            <p className="text-xs text-zinc-500">{name} · {data?.total ?? "…"} righe (in attesa di analisi/approvazione)</p>
          </div>
          <button onClick={onClose} data-testid="rows-modal-close" className="rounded-lg border border-white/10 p-1.5 text-zinc-300"><X className="h-4 w-4" /></button>
        </div>
        <div className="max-h-[70vh] overflow-auto p-4">
          {!data && <p className="text-zinc-500">Caricamento…</p>}
          {data && data.rows.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessuna riga estratta.</p>}
          {data && data.rows.length > 0 && (
            <table className="w-full min-w-[860px] border-separate border-spacing-y-1 text-xs">
              <thead>
                <tr className="text-left uppercase tracking-wider text-zinc-500">
                  <th className="px-2 py-1">Modello</th><th className="px-2">Brand</th><th className="px-2">SKU</th>
                  <th className="px-2">EAN</th><th className="px-2">Memoria</th><th className="px-2">Colore</th>
                  <th className="px-2">Prezzo</th><th className="px-2">+ Montaggio</th><th className="px-2">Q.tà</th><th className="px-2">Pag.</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.id} data-testid={`row-item-${r.id}`} className="bg-[#0B0E13]">
                    <td className="max-w-[260px] truncate px-2 py-1.5 text-white" title={r.model || r.raw_line}>{r.model || <span className="text-zinc-600">—</span>}</td>
                    <td className="px-2 text-zinc-400">{r.brand || "—"}</td>
                    <td className="px-2 text-zinc-400">{r.sku || "—"}</td>
                    <td className="px-2 text-zinc-400">{r.ean || "—"}</td>
                    <td className="px-2 text-zinc-400">{r.memory || "—"}</td>
                    <td className="px-2 text-zinc-400">{r.color || "—"}</td>
                    <td className="px-2 text-zinc-200">{fmtEur(r.supplier_cost_cents)}</td>
                    <td className="px-2 text-zinc-200">{fmtEur(r.installation_cost_cents)}</td>
                    <td className="px-2 text-zinc-400">{r.quantity ?? "—"}</td>
                    <td className="px-2 text-zinc-500">{r.source_page ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const CLASS_META = {
  NEW: { label: "Nuovi", icon: "🆕", cls: "bg-emerald-500/15 text-emerald-400" },
  MODIFIED: { label: "Modificati", icon: "🔄", cls: "bg-amber-500/15 text-amber-400" },
  UNCHANGED: { label: "Invariati", icon: "🟢", cls: "bg-zinc-500/15 text-zinc-400" },
  REMOVED: { label: "Rimossi", icon: "❌", cls: "bg-rose-500/15 text-rose-400" },
  DUPLICATE: { label: "Duplicati", icon: "⚠️", cls: "bg-orange-500/15 text-orange-400" },
  ANOMALY: { label: "Anomalie", icon: "⚠️", cls: "bg-red-500/15 text-red-400" },
  INCOMPLETE: { label: "Incompleti", icon: "⚠️", cls: "bg-orange-500/15 text-orange-400" },
  UNCERTAIN_MATCH: { label: "Match incerti", icon: "⚠️", cls: "bg-orange-500/15 text-orange-400" },
};
const DEC_CLS = { APPROVED: "bg-emerald-600 text-white", REJECTED: "bg-rose-600 text-white", IGNORED: "bg-zinc-600 text-white", PENDING: "" };
const pctStr = (n) => (n === null || n === undefined ? "" : `${n > 0 ? "+" : ""}${n}%`);
const deltaEur = (c) => (c === null || c === undefined ? "" : `${c > 0 ? "+" : ""}${(c / 100).toFixed(2).replace(".", ",")} €`);

function Field({ label, a, b, hl }) {
  if (a === undefined && b === undefined) return null;
  const chg = hl && String(a ?? "") !== String(b ?? "");
  return (
    <div className={`flex justify-between gap-2 ${chg ? "text-amber-300" : ""}`}>
      <span className="text-zinc-500">{label}</span><span className="truncate">{b ?? a ?? "—"}</span>
    </div>
  );
}

function DiffItemCard({ it, selected, onSelect, onDecide, busy }) {
  const m = CLASS_META[it.classification] || {};
  const o = it.old || {}, n = it.new || {}, d = it.diff || {};
  const dec = it.decision || "PENDING";
  return (
    <div data-testid={`diff-item-${it.id}`} className="rounded-xl border border-white/10 bg-[#0B0E13] p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <input type="checkbox" checked={selected} onChange={(e) => onSelect(it.id, e.target.checked)} data-testid={`diff-select-${it.id}`} className="h-4 w-4 accent-[#2563EB]" />
        <span className={`rounded-full px-2 py-0.5 font-semibold ${m.cls}`}>{m.icon} {it.classification}</span>
        {it.change_type && it.change_type !== "NONE" && <span className="rounded-full bg-white/5 px-2 py-0.5 text-zinc-400">{it.change_type}</span>}
        {it.matched_by && it.matched_by !== "NONE" && <span className="text-[10px] text-zinc-500">match: {it.matched_by} ({it.confidence})</span>}
        {it.anomalies?.map((a) => <span key={a} className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400">⚠️ {a}</span>)}
        {dec !== "PENDING" && <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold ${DEC_CLS[dec]}`}>{dec}</span>}
      </div>
      {it.reason && <p className="mt-1.5 text-[11px] text-zinc-400">{it.reason}</p>}
      <div className="mt-2 grid grid-cols-1 gap-3 text-[11px] sm:grid-cols-2">
        <div className="rounded-lg border border-white/5 p-2">
          <p className="mb-1 font-mono uppercase tracking-wider text-zinc-600">Vecchio</p>
          {it.old ? (<>
            <Field label="Articolo" a={[o.brand, o.model, o.variant].filter(Boolean).join(" ")} />
            <Field label="Qualità" a={o.quality || "—"} /><Field label="SKU" a={o.sku || "—"} /><Field label="EAN" a={o.ean || "—"} />
            <Field label="Prezzo" a={fmtEur(o.supplier_cost_cents)} /><Field label="Montaggio" a={fmtEur(o.installation_cost_cents)} />
            <Field label="Disp." a={o.availability || "—"} />
          </>) : <p className="text-zinc-600">—</p>}
        </div>
        <div className="rounded-lg border border-white/5 p-2">
          <p className="mb-1 font-mono uppercase tracking-wider text-zinc-600">Nuovo</p>
          {it.new ? (<>
            <Field label="Articolo" b={[n.brand, n.model, n.variant].filter(Boolean).join(" ")} a={[o.brand, o.model, o.variant].filter(Boolean).join(" ")} hl={!!it.old} />
            <Field label="Qualità" b={n.quality || "—"} a={o.quality} hl={!!it.old} /><Field label="SKU" b={n.sku || "—"} a={o.sku} hl={!!it.old} /><Field label="EAN" b={n.ean || "—"} a={o.ean} hl={!!it.old} />
            <Field label="Prezzo" b={fmtEur(n.supplier_cost_cents)} a={fmtEur(o.supplier_cost_cents)} hl={!!it.old} />
            <Field label="Montaggio" b={fmtEur(n.installation_cost_cents)} a={fmtEur(o.installation_cost_cents)} hl={!!it.old} />
            <Field label="Disp." b={n.availability || "—"} a={o.availability} hl={!!it.old} />
          </>) : <p className="text-zinc-600">—</p>}
        </div>
      </div>
      {d && (d.cost_changed || d.install_changed) && (
        <div className="mt-2 rounded-lg bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-300" data-testid={`diff-price-${it.id}`}>
          {d.price_label}: {d.cost_changed && <span>{fmtEur(d.cost_old)} → {fmtEur(d.cost_new)} ({deltaEur(d.cost_delta)}{d.cost_pct !== null ? `, ${pctStr(d.cost_pct)}` : ""})</span>}
          {d.install_changed && <span className="ml-2">Montaggio {fmtEur(d.install_old)} → {fmtEur(d.install_new)}</span>}
        </div>
      )}
      <div className="mt-2 flex gap-1.5">
        <button data-testid={`diff-approve-${it.id}`} disabled={busy} onClick={() => onDecide(it.id, "APPROVED")} className="flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"><Check className="h-3.5 w-3.5" /> Approva</button>
        <button data-testid={`diff-reject-${it.id}`} disabled={busy} onClick={() => onDecide(it.id, "REJECTED")} className="flex items-center gap-1 rounded-lg bg-rose-600 px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50"><Ban className="h-3.5 w-3.5" /> Rifiuta</button>
        <button data-testid={`diff-ignore-${it.id}`} disabled={busy} onClick={() => onDecide(it.id, "IGNORED")} className="flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1 text-xs text-zinc-300 disabled:opacity-50"><MinusCircle className="h-3.5 w-3.5" /> Ignora</button>
      </div>
    </div>
  );
}

const APPLY_ACTION = {
  UPDATE: { label: "Aggiorna costo fornitore", cls: "bg-amber-500/15 text-amber-400" },
  CREATE_PART: { label: "Crea ricambio", cls: "bg-emerald-500/15 text-emerald-400" },
  CREATE_PRODUCT_DRAFT: { label: "Crea prodotto (bozza, non pubblicato)", cls: "bg-emerald-500/15 text-emerald-400" },
  DISCONTINUE: { label: "Marca fuori listino fornitore", cls: "bg-rose-500/15 text-rose-400" },
};
const FIELD_LABEL = {
  cost_cents: "Prezzo fornitore", installation_cost_cents: "Montaggio", availability: "Disponibilità",
  lead_time: "Lead time", quality: "Qualità", manufacturer_code: "Cod. produttore",
  ean: "EAN", sku: "SKU", supplier_discontinued: "Fuori listino",
};
const fmtVal = (f, v) => {
  if (v === null || v === undefined) return "—";
  if (f.endsWith("_cents")) return fmtEur(v);
  if (typeof v === "boolean") return v ? "Sì" : "No";
  return String(v);
};

function BatchRow({ b, busy, onRollbackBatch, onChanged }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState(null);
  const [lbusy, setLbusy] = useState("");
  const toggle = async () => {
    const nx = !open; setOpen(nx);
    if (nx && !logs) { try { const r = await getApplyBatch(b.id); setLogs(r.logs || []); } catch { setLogs([]); } }
  };
  const rbItem = async (logId) => {
    setLbusy(logId);
    try { await rollbackItem(logId); toast.success("Voce annullata"); const r = await getApplyBatch(b.id); setLogs(r.logs || []); onChanged && (await onChanged()); }
    catch (e) { toast.error(errMsg(e) || "Errore"); }
    finally { setLbusy(""); }
  };
  return (
    <div data-testid={`apply-batch-${b.id}`} className="mb-1.5 rounded-lg border border-white/10 p-2.5 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={toggle} data-testid={`apply-batch-expand-${b.id}`} className="text-zinc-400"><ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></button>
        <span className={`rounded-full px-2 py-0.5 font-semibold ${b.status === "ROLLED_BACK" ? "bg-zinc-600 text-white" : "bg-emerald-600 text-white"}`}>{b.status}</span>
        <span className="text-zinc-400">applicato da {b.applied_by} il {fmtDate(b.applied_at)}</span>
        <span className="text-zinc-500">🆕{b.counts?.new || 0} 🔄{b.counts?.modified || 0} ❌{b.counts?.removed || 0}</span>
        {b.status !== "ROLLED_BACK" && (
          <button data-testid={`apply-rollback-${b.id}`} onClick={() => onRollbackBatch(b.id)} disabled={busy === b.id}
            className="ml-auto flex items-center gap-1 rounded-lg border border-rose-500/40 px-2.5 py-1 text-rose-300 disabled:opacity-50">
            {busy === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />} Rollback batch
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 space-y-1 border-t border-white/5 pt-2">
          {!logs && <p className="text-zinc-600">Caricamento voci…</p>}
          {logs && logs.length === 0 && <p className="text-zinc-600">Nessuna voce.</p>}
          {logs && logs.map((l) => (
            <div key={l.id} data-testid={`apply-log-${l.id}`} className="flex flex-wrap items-center gap-2 rounded bg-[#0B0E13] px-2 py-1.5">
              <span className="text-zinc-300">{l.label || "—"}</span>
              <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-zinc-400">{l.action}</span>
              {(l.field_changes || []).map((c, i) => (
                <span key={i} className="text-[10px] text-zinc-500">{FIELD_LABEL[c.field] || c.field}: {fmtVal(c.field, c.before)}→{fmtVal(c.field, c.after)}</span>
              ))}
              {l.rolled_back ? <span className="ml-auto rounded-full bg-zinc-600 px-2 py-0.5 text-[10px] text-white">ANNULLATA</span> : (
                <button data-testid={`apply-log-rollback-${l.id}`} onClick={() => rbItem(l.id)} disabled={lbusy === l.id}
                  className="ml-auto flex items-center gap-1 rounded border border-rose-500/40 px-2 py-0.5 text-[10px] text-rose-300 disabled:opacity-50">
                  {lbusy === l.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Rollback voce
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ApplyModal({ diff, onClose }) {
  const [pv, setPv] = useState(null);
  const [batches, setBatches] = useState([]);
  const [busy, setBusy] = useState("");
  const loadPreview = async () => setPv(await applyPreview(diff.id));
  const loadBatches = async () => setBatches(await listApplyBatches(diff.supplier_id));
  useEffect(() => { loadPreview().catch(() => setPv({ counts: {}, to_apply: [], excluded: [] })); loadBatches().catch(() => {}); }, [diff.id]);

  const apply = async () => {
    setBusy("apply");
    try { const r = await applyDiff(diff.id); toast.success(`Applicati ${r.counts.applied} elementi`); toast.message(r.note); await loadPreview(); await loadBatches(); }
    catch (e) { toast.error(errMsg(e) || "Applicazione non riuscita"); }
    finally { setBusy(""); }
  };
  const doRollback = async (bid) => {
    setBusy(bid);
    try { const r = await rollbackBatch(bid); toast.success(`Ripristinati ${r.restored} elementi`); await loadPreview(); await loadBatches(); }
    catch (e) { toast.error(errMsg(e) || "Rollback non riuscito"); }
    finally { setBusy(""); }
  };
  const recalc = async () => {
    setBusy("recalc");
    try { const r = await recalcPrices({}); toast.success(`Pricing Engine: ${r.aggiornati} prezzi ricalcolati`); toast.message(r.note); }
    catch (e) { toast.error(errMsg(e) || "Ricalcolo non riuscito"); }
    finally { setBusy(""); }
  };

  const c = pv?.counts || {};
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#11141A] text-white" data-testid="apply-modal">
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div>
            <h3 className="text-lg font-bold">Applicazione controllata</h3>
            <p className="text-xs text-zinc-500">Aggiorna SOLO il costo fornitore/dati. Il prezzo di vendita sarà ricalcolato dal Pricing Engine.</p>
          </div>
          <button onClick={onClose} data-testid="apply-close" className="rounded-lg border border-white/10 p-1.5 text-zinc-300"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-auto p-4">
          {!pv && <p className="text-zinc-500">Caricamento anteprima…</p>}
          {pv && (<>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-white/5 px-3 py-1.5">Da applicare: <b>{c.to_apply || 0}</b></span>
              <span className="rounded-full bg-emerald-500/15 px-3 py-1.5 text-emerald-400">🆕 Nuovi: {c.new || 0}</span>
              <span className="rounded-full bg-amber-500/15 px-3 py-1.5 text-amber-400">🔄 Modificati: {c.modified || 0}</span>
              <span className="rounded-full bg-rose-500/15 px-3 py-1.5 text-rose-400">❌ Rimossi: {c.removed || 0}</span>
              {!!c.anomalies && <span className="rounded-full bg-red-500/15 px-3 py-1.5 text-red-400">⚠️ Con anomalie: {c.anomalies}</span>}
              <span className="rounded-full bg-zinc-500/15 px-3 py-1.5 text-zinc-400">Esclusi: {c.excluded || 0}</span>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Modifiche da applicare</p>
              {pv.to_apply.length === 0 && <p className="rounded-lg border border-white/5 p-3 text-sm text-zinc-500">Nessuna modifica approvata applicabile. Approva prima gli elementi nel confronto.</p>}
              {pv.to_apply.map((p) => {
                const a = APPLY_ACTION[p.action] || {};
                const label = (p.new || p.old || {});
                return (
                  <div key={p.diff_item_id} data-testid={`apply-item-${p.diff_item_id}`} className="rounded-xl border border-white/10 bg-[#0B0E13] p-3">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`rounded-full px-2 py-0.5 font-semibold ${a.cls}`}>{a.label}</span>
                      <span className="text-white">{[label.brand, label.model, label.variant].filter(Boolean).join(" ")}</span>
                      {p.anomalies?.map((x) => <span key={x} className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400">⚠️ {x}</span>)}
                    </div>
                    <div className="mt-1.5 space-y-0.5 text-[11px]">
                      {p.changes.map((ch, i) => (
                        <div key={i} className="flex gap-2 text-zinc-400"><span className="w-32 text-zinc-500">{FIELD_LABEL[ch.field] || ch.field}</span>
                          <span>{fmtVal(ch.field, ch.before)}</span><span className="text-zinc-600">→</span><span className="text-emerald-300">{fmtVal(ch.field, ch.after)}</span></div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {pv.excluded.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Esclusi (verifica manuale)</p>
                {pv.excluded.map((e) => (
                  <div key={e.diff_item_id} data-testid={`apply-excluded-${e.diff_item_id}`} className="flex items-center gap-2 rounded-lg bg-orange-500/10 px-3 py-1.5 text-[11px] text-orange-300">
                    <AlertTriangle className="h-3.5 w-3.5" /> <b>{e.classification}</b> — {(e.new || e.old || {}).model} · {e.exclude_reason}
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-white/10 pt-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-zinc-500"><History className="h-3.5 w-3.5" /> Storico applicazioni</p>
              {batches.length === 0 && <p className="text-sm text-zinc-600">Nessuna applicazione eseguita.</p>}
              {batches.map((b) => (
                <BatchRow key={b.id} b={b} busy={busy} onRollbackBatch={doRollback}
                  onChanged={async () => { await loadPreview(); await loadBatches(); }} />
              ))}
            </div>
          </>)}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-white/10 p-4">
          <button data-testid="apply-run" onClick={apply} disabled={busy === "apply" || !(pv?.to_apply?.length)}
            className="flex items-center gap-2 rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold disabled:opacity-40">
            {busy === "apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackageCheck className="h-4 w-4" />} Applica modifiche approvate
          </button>
          <button data-testid="apply-recalc" onClick={recalc} disabled={busy === "recalc"}
            className="flex items-center gap-2 rounded-lg border border-emerald-500/40 px-4 py-2 text-sm font-semibold text-emerald-300 disabled:opacity-50" title="Ricalcola i prezzi di vendita dei prodotti secondo le regole del Pricing Engine">
            {busy === "recalc" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Ricalcola prezzi vendita
          </button>
          <span className="ml-auto text-[11px] text-zinc-500">sale_price / promo / stock NON vengono toccati dall'apply. Usa "Ricalcola prezzi" per aggiornare i prezzi di vendita.</span>
        </div>
      </div>
    </div>
  );
}

function DiffModal({ pl, onClose, autoRun }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState(null); // {classification} | {anomaly:true} | null
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const autoRan = useRef(false);
  const lid = pl.id;

  const load = async (f) => {
    const params = {};
    if (f?.classification) params.classification = f.classification;
    if (f?.anomaly) params.anomaly = true;
    const r = await getDiff(lid, params);
    setData(r);
  };
  useEffect(() => { load(null).catch(() => setData({ diff: null, items: [] })); }, [lid]);
  useEffect(() => {
    if (autoRun && data && !data.diff && !autoRan.current) { autoRan.current = true; run(); }
  }, [autoRun, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setBusy(true);
    try { await buildDiff(lid); await load(filter); toast.success("Confronto completato"); }
    catch (e) { toast.error(errMsg(e) || "Confronto non riuscito"); }
    finally { setBusy(false); }
  };
  const decide = async (id, decision) => {
    setBusy(true);
    try { await diffItemDecision(id, decision); await load(filter); }
    catch (e) { toast.error(errMsg(e) || "Errore"); }
    finally { setBusy(false); }
  };
  const bulk = async (action) => {
    setBusy(true);
    try {
      const body = { action };
      if (action !== "APPROVE_SAFE") body.item_ids = [...sel];
      const r = await diffBulk(data.diff.id, body);
      toast.success(`${r.updated} elementi aggiornati`); setSel(new Set()); await load(filter);
    } catch (e) { toast.error(errMsg(e) || "Errore"); }
    finally { setBusy(false); }
  };
  const onSelect = (id, on) => setSel((p) => { const s = new Set(p); on ? s.add(id) : s.delete(id); return s; });
  const chip = (key) => setFilter((prev) => {
    const nf = key === "ANOMALY" ? { anomaly: true } : { classification: key };
    const same = prev && (prev.classification === key || (key === "ANOMALY" && prev.anomaly));
    const val = same ? null : nf; load(val); return val;
  });

  const diff = data?.diff;
  const counts = diff?.counts || {};
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#11141A] text-white" data-testid="diff-modal">
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div>
            <h3 className="text-lg font-bold">Analisi Listino — Confronto versioni</h3>
            {diff ? <p className="text-xs text-zinc-500">Vecchio: {diff.old_list_name} ({fmtDate(diff.old_list_date)}) → Nuovo: {diff.new_list_name} ({fmtDate(diff.new_list_date)}) · Stato: <span className="font-semibold text-zinc-300">{diff.status}</span></p>
              : <p className="text-xs text-zinc-500">{pl.name}</p>}
          </div>
          <button onClick={onClose} data-testid="diff-close" className="rounded-lg border border-white/10 p-1.5 text-zinc-300"><X className="h-4 w-4" /></button>
        </div>

        {!diff && (
          <div className="p-8 text-center">
            <p className="mb-4 text-sm text-zinc-400">Nessun confronto eseguito. Genera l'analisi delle differenze rispetto alla versione precedente.</p>
            <button data-testid="diff-run" onClick={run} disabled={busy} className="mx-auto flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitCompare className="h-4 w-4" />} Analizza differenze
            </button>
          </div>
        )}

        {diff && (<>
          <div className="flex flex-wrap gap-2 border-b border-white/10 p-4">
            {["NEW", "MODIFIED", "UNCHANGED", "REMOVED", "DUPLICATE", "ANOMALY", "INCOMPLETE", "UNCERTAIN_MATCH"].map((k) => {
              const active = filter && (filter.classification === k || (k === "ANOMALY" && filter.anomaly));
              return (
                <button key={k} data-testid={`diff-chip-${k}`} onClick={() => chip(k)}
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${CLASS_META[k].cls} ${active ? "ring-2 ring-white/40" : ""}`}>
                  {CLASS_META[k].icon} {CLASS_META[k].label} <span className="rounded-full bg-black/30 px-1.5">{counts[k] || 0}</span>
                </button>
              );
            })}
            <button data-testid="diff-run-again" onClick={run} disabled={busy} className="ml-auto flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300"><GitCompare className="h-3.5 w-3.5" /> Ricalcola</button>
          </div>

          <div className="flex-1 space-y-2 overflow-auto p-4">
            {(data.items || []).length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessun elemento in questa categoria.</p>}
            {(data.items || []).map((it) => (
              <DiffItemCard key={it.id} it={it} selected={sel.has(it.id)} onSelect={onSelect} onDecide={decide} busy={busy} />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-white/10 p-4">
            <button data-testid="diff-approve-safe" onClick={() => bulk("APPROVE_SAFE")} disabled={busy} className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold disabled:opacity-50"><CheckCircle2 className="h-4 w-4" /> Approva tutte le modifiche sicure</button>
            <button data-testid="diff-approve-sel" onClick={() => bulk("APPROVE_SELECTED")} disabled={busy || !sel.size} className="rounded-lg border border-emerald-600/40 px-3 py-2 text-xs text-emerald-300 disabled:opacity-40">Approva selezionati ({sel.size})</button>
            <button data-testid="diff-reject-sel" onClick={() => bulk("REJECT_SELECTED")} disabled={busy || !sel.size} className="rounded-lg border border-rose-600/40 px-3 py-2 text-xs text-rose-300 disabled:opacity-40">Rifiuta selezionati</button>
            <button data-testid="diff-open-apply" onClick={() => setShowApply(true)} className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white"><Rocket className="h-4 w-4" /> Anteprima applicazione</button>
            <span className="ml-auto text-[11px] text-zinc-500">Le decisioni NON applicano modifiche al catalogo/prezzi (proposta in attesa).</span>
          </div>
        </>)}
        {showApply && diff && <ApplyModal diff={diff} onClose={() => { setShowApply(false); load(filter); }} />}
      </div>
    </div>
  );
}

function PriceListRow({ pl, onChanged, versionLabel, isCurrent }) {
  const [busy, setBusy] = useState("");
  const updateInputRef = useRef(null);
  const [showRows, setShowRows] = useState(false);
  const [showDiff, setShowDiff] = useState(false);
  const inputRef = useRef(null);
  const st = pl.import_status;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okExt = /\.(pdf|xls|xlsx|csv)$/i.test(file.name);
    if (!okExt) return toast.error("Formato non ammesso. Usa PDF, XLS, XLSX o CSV.");
    if (file.size > 25 * 1024 * 1024) return toast.error("File troppo grande (max 25 MB).");
    setBusy("upload");
    try {
      const data_b64 = await fileToB64(file);
      await uploadPriceListFile(pl.id, { filename: file.name, mime: file.type, data_b64 });
      toast.success("File caricato"); onChanged();
    } catch (err) { toast.error(errMsg(err) || "Upload non riuscito"); }
    finally { setBusy(""); }
  };

  const analyze = async () => {
    setBusy("analyze");
    try {
      const r = await analyzePriceList(pl.id);
      if (r.import_status === "ERROR") toast.error("Analisi con errori: " + (r.parse_errors?.[0] || ""));
      else toast.success(`Analisi completata: ${r.parsed_rows} righe`);
      onChanged();
    } catch (err) { toast.error(errMsg(err) || "Analisi non riuscita"); }
    finally { setBusy(""); }
  };

  const onUpdateFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(pdf|xls|xlsx|csv)$/i.test(file.name)) return toast.error("Formato non ammesso. Usa PDF, XLS, XLSX o CSV.");
    if (file.size > 25 * 1024 * 1024) return toast.error("File troppo grande (max 25 MB).");
    setBusy("update");
    try {
      const data_b64 = await fileToB64(file);
      const nv = await newListVersion(pl.id, {});               // nuova VERSIONE, previous_list_id = pl.id
      await uploadPriceListFile(nv.id, { filename: file.name, mime: file.type, data_b64 });
      const r = await analyzePriceList(nv.id);                  // ANALYZING -> PARSED/READY_FOR_REVIEW
      if (r.import_status === "ERROR") toast.error("Analisi con errori: " + (r.parse_errors?.[0] || ""));
      else toast.success(`Nuova versione creata e analizzata: ${r.parsed_rows} righe. Apro il confronto…`);
      onChanged?.(nv.id);                                       // reload + apre il DIFF sulla nuova versione
    } catch (err) { toast.error(errMsg(err) || "Aggiornamento listino non riuscito"); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid={`pricelist-row-${pl.id}`} className="rounded-xl border border-white/10 bg-[#11141A] p-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <FileStack className="h-4 w-4 text-zinc-500" />
        <span className="font-medium text-white">{pl.name}</span>
        {versionLabel && <span data-testid={`pl-version-${pl.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${isCurrent ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-zinc-400"}`}>{versionLabel}{isCurrent ? " · ATTUALE" : ""}</span>}
        <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-zinc-400">{pl.list_type}</span>
        {st && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${IMPORT_BADGE[st] || "bg-zinc-500/15 text-zinc-400"}`} data-testid={`pl-status-${pl.id}`}>{st}</span>}
        <span className="text-xs text-zinc-500">{fmtDate(pl.list_date || pl.imported_at)}</span>
        {pl.source_filename && <span className="text-xs text-zinc-500">{pl.source_filename}</span>}
        {typeof pl.parsed_rows === "number" && pl.parsed_rows > 0 && <span className="text-xs text-emerald-400">{pl.parsed_rows} righe</span>}
        {pl.previous_list_id && <span className="text-[10px] text-zinc-600">↳ collegato a versione precedente</span>}
      </div>
      {pl.parse_errors?.length > 0 && (
        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-rose-500/10 px-2.5 py-1.5 text-[11px] text-rose-300" data-testid={`pl-errors-${pl.id}`}>
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{pl.parse_errors.slice(0, 3).join(" · ")}</span>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={updateInputRef} type="file" accept={ACCEPT} onChange={onUpdateFile} className="hidden" data-testid={`pl-update-input-${pl.id}`} />
        <button data-testid={`pl-update-${pl.id}`} onClick={() => updateInputRef.current?.click()} disabled={!!busy}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50" title="Carica un nuovo PDF come nuova versione (mantiene la precedente)">
          {busy === "update" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} 🔄 Aggiorna listino
        </button>
        <input ref={inputRef} type="file" accept={ACCEPT} onChange={onFile} className="hidden" data-testid={`pl-file-input-${pl.id}`} />
        <button data-testid={`pl-upload-${pl.id}`} onClick={() => inputRef.current?.click()} disabled={!!busy}
          className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-zinc-200 disabled:opacity-50">
          {busy === "upload" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {pl.source_file_path || pl.source_filename ? "Sostituisci file" : "Carica file"}
        </button>
        {(pl.source_filename || st) && (
          <a data-testid={`pl-download-${pl.id}`} href={priceListFileUrl(pl.id)} target="_blank" rel="noreferrer"
            className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-zinc-200"><FileDown className="h-3.5 w-3.5" /> Scarica originale</a>
        )}
        <button data-testid={`pl-analyze-${pl.id}`} onClick={analyze} disabled={!!busy || !(pl.source_filename || st)}
          className="flex items-center gap-1.5 rounded-lg bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40">
          {busy === "analyze" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />} Analizza
        </button>
        {pl.parsed_rows > 0 && (
          <button data-testid={`pl-view-rows-${pl.id}`} onClick={() => setShowRows(true)}
            className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-zinc-200"><Eye className="h-3.5 w-3.5" /> Vedi righe</button>
        )}
        {pl.parsed_rows > 0 && pl.previous_list_id && (
          <button data-testid={`pl-diff-${pl.id}`} onClick={() => setShowDiff(true)}
            className="flex items-center gap-1.5 rounded-lg border border-indigo-500/40 px-3 py-1.5 text-xs font-semibold text-indigo-300"><GitCompare className="h-3.5 w-3.5" /> Confronta versione precedente</button>
        )}
        {st === "READY_FOR_REVIEW" && <span className="flex items-center gap-1 text-[11px] text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Pronto per la revisione</span>}
      </div>
      {showRows && <RowsModal lid={pl.id} name={pl.name} onClose={() => setShowRows(false)} />}
      {showDiff && <DiffModal pl={pl} onClose={() => setShowDiff(false)} />}
    </div>
  );
}

const DIFF_STATUS_CLS = {
  READY_FOR_REVIEW: "bg-indigo-500/15 text-indigo-400", PARTIALLY_APPROVED: "bg-amber-500/15 text-amber-400",
  APPROVED: "bg-emerald-500/15 text-emerald-400", REJECTED: "bg-rose-500/15 text-rose-400",
  APPLIED: "bg-emerald-600 text-white", PARTIALLY_APPLIED: "bg-amber-600 text-white", ANALYZING: "bg-zinc-500/15 text-zinc-400",
};

function HistoryModal({ sid, onClose }) {
  const [diffs, setDiffs] = useState(null);
  useEffect(() => { supplierDiffs(sid).then(setDiffs).catch(() => setDiffs([])); }, [sid]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#11141A] text-white" data-testid="history-modal">
        <div className="flex items-center justify-between border-b border-white/10 p-4">
          <div><h3 className="text-lg font-bold">Storico confronti & applicazioni</h3>
            <p className="text-xs text-zinc-500">Tutti i confronti eseguiti per questo fornitore, con decisioni e applicazioni.</p></div>
          <button onClick={onClose} data-testid="history-close" className="rounded-lg border border-white/10 p-1.5 text-zinc-300"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-auto p-4">
          {!diffs && <p className="text-zinc-500">Caricamento…</p>}
          {diffs && diffs.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessun confronto eseguito finora.</p>}
          {diffs && diffs.map((d) => {
            const c = d.counts || {};
            return (
              <div key={d.id} data-testid={`history-diff-${d.id}`} className="rounded-xl border border-white/10 bg-[#0B0E13] p-3">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className={`rounded-full px-2 py-0.5 font-semibold ${DIFF_STATUS_CLS[d.status] || "bg-zinc-500/15 text-zinc-400"}`}>{d.status}</span>
                  <span className="text-zinc-300">{d.old_list_name} ({fmtDate(d.old_list_date)}) → {d.new_list_name} ({fmtDate(d.new_list_date)})</span>
                  <span className="ml-auto text-[11px] text-zinc-500">creato {fmtDate(d.created_at)}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-2 text-[11px] text-zinc-400">
                  <span>🆕 {c.NEW || 0}</span><span>🔄 {c.MODIFIED || 0}</span><span>🟢 {c.UNCHANGED || 0}</span>
                  <span>❌ {c.REMOVED || 0}</span><span>⚠️ dup {c.DUPLICATE || 0}</span><span>⚠️ anom {c.ANOMALY || 0}</span>
                  <span>⚠️ incerti {c.UNCERTAIN_MATCH || 0}</span>
                  {d.decided_by && <span className="text-zinc-500">· deciso da {d.decided_by} il {fmtDate(d.decided_at)}</span>}
                </div>
                {(d.batches || []).length > 0 && (
                  <div className="mt-2 space-y-1 border-t border-white/5 pt-2">
                    {d.batches.map((b) => (
                      <div key={b.id} className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className={`rounded-full px-2 py-0.5 font-semibold ${b.status === "ROLLED_BACK" ? "bg-zinc-600 text-white" : "bg-emerald-600 text-white"}`}>{b.status}</span>
                        <span className="text-zinc-400">applicato da {b.applied_by} il {fmtDate(b.applied_at)}</span>
                        <span className="text-zinc-500">🆕{b.counts?.new || 0} 🔄{b.counts?.modified || 0} ❌{b.counts?.removed || 0}</span>
                        {b.rolled_back_at && <span className="text-zinc-500">· annullato il {fmtDate(b.rolled_back_at)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SupplierDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const [addList, setAddList] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [diffForLid, setDiffForLid] = useState(null);
  const [supBusy, setSupBusy] = useState(false);
  const supUpdateRef = useRef(null);
  const nwgInputRef = useRef(null);
  const [nwgKey, setNwgKey] = useState(null);
  const load = () => supplierDetail(id).then(setData).catch(() => {});
  useEffect(() => { load(); }, [id]);
  const versionMap = useMemo(() => {
    const groups = {};
    (data?.price_lists || []).forEach((pl) => { const k = (pl.name || "").trim().toLowerCase(); (groups[k] = groups[k] || []).push(pl); });
    const map = {};
    Object.values(groups).forEach((arr) => {
      arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      arr.forEach((pl, i) => { map[pl.id] = { label: `V${i + 1}`, current: i === arr.length - 1 }; });
    });
    return map;
  }, [data]);
  const latestList = useMemo(() => {
    const arr = [...(data?.price_lists || [])];
    arr.sort((a, b) => (b.created_at || b.list_date || "").localeCompare(a.created_at || a.list_date || ""));
    return arr[0] || null;
  }, [data]);
  const onSupplierUpdateFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.(pdf|xls|xlsx|csv)$/i.test(file.name)) return toast.error("Formato non ammesso. Usa PDF, XLS, XLSX o CSV.");
    if (file.size > 25 * 1024 * 1024) return toast.error("File troppo grande (max 25 MB).");
    setSupBusy(true);
    try {
      const data_b64 = await fileToB64(file);
      const r = await autoUpdatePriceList(id, { filename: file.name, mime: file.type, data_b64 });
      toast.success(`✅ Listino aggiornato · ${new Date().toLocaleDateString("it-IT")} · Righe importate: ${r.parsed_rows} · Listino precedente archiviato automaticamente.`);
      load();
    } catch (err) { toast.error(errMsg(err) || "Aggiornamento non completato: il file non può essere interpretato correttamente."); }
    finally { setSupBusy(false); }
  };
  const onRestore = async (pl) => {
    if (!window.confirm("Ripristinare questa versione come listino attivo? La versione attiva corrente verrà archiviata.")) return;
    try {
      if ((pl.list_type || "").startsWith("NWG:")) await nwgRestoreList(pl.id);
      else await restorePriceList(pl.id);
      toast.success("Versione ripristinata come ATTIVA."); load();
    } catch (err) { toast.error(errMsg(err) || "Ripristino non riuscito"); }
  };
  const onNwgFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; const key = nwgKey; setNwgKey(null);
    if (!file || !key) return;
    if (!/\.pdf$/i.test(file.name)) return toast.error("Per NewWaveGames carica un PDF.");
    if (file.size > 25 * 1024 * 1024) return toast.error("File troppo grande (max 25 MB).");
    setSupBusy(true);
    try {
      const data_b64 = await fileToB64(file);
      const r = await nwgReplaceList({ slug: data.supplier.slug, list_key: key, filename: file.name, data_b64 });
      toast.success(`✅ ${r.list} aggiornato · ${new Date().toLocaleDateString("it-IT")} · Righe importate: ${r.rows} · listino sostituito.`);
      load();
    } catch (err) { toast.error(errMsg(err) || "Aggiornamento non completato: il file non può essere interpretato correttamente."); }
    finally { setSupBusy(false); }
  };
  if (!data) return <p className="text-zinc-500">Caricamento…</p>;
  const s = data.supplier;
  const supplierLists = SUPPLIER_LISTS[s.slug] || [];
  const nwgInfo = (key) => {
    const pls = (data.price_lists || []).filter((p) => p.list_type === `NWG:${key}`);
    const act = pls.find((p) => p.status === "ACTIVE") || pls[0];
    if (!act) return null;
    return { rows: act.parsed_rows, date: act.imported_at || act.list_date || act.created_at };
  };
  return (
    <div className="space-y-4" data-testid="supplier-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-zinc-400"><ArrowLeft className="h-4 w-4" /> Torna ai fornitori</button>
      <div className="rounded-xl border border-white/10 bg-[#11141A] p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Truck className="h-5 w-5 text-[#2563EB]" />
          <span className="text-lg font-bold text-white">{s.name}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] ${s.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-400"}`}>{s.active ? "ATTIVO" : "DISATTIVO"}</span>
          <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs text-zinc-400">{s.source_type || "PDF"}</span>
          <button data-testid="supplier-detail-edit" onClick={() => setEdit(true)} className="ml-auto flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-zinc-200"><Pencil className="h-3.5 w-3.5" /> Modifica</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
          {s.category && <span>Categoria: {s.category}</span>}
          {s.website && <span>Web: {s.website}</span>}
          {s.contact_email && <span>Email: {s.contact_email}</span>}
          {s.phone && <span>Tel: {s.phone}</span>}
        </div>
        {s.note && <p className="mt-2 text-xs text-zinc-400">{s.note}</p>}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Storico listini / versioni</h3>
        <div className="flex flex-wrap items-center gap-2">
          {supplierLists.length > 0 ? (
            <>
              <input ref={nwgInputRef} type="file" accept=".pdf" onChange={onNwgFile} className="hidden" data-testid="nwg-update-input" />
              {supplierLists.map((l) => {
                const info = nwgInfo(l.key);
                return (
                  <div key={l.key} className="flex flex-col items-start gap-0.5">
                    <button data-testid={`nwg-update-${l.key}`} disabled={supBusy} onClick={() => { setNwgKey(l.key); nwgInputRef.current?.click(); }}
                      title={`Aggiorna il listino "${l.label}": sostituisce completamente solo questo listino`}
                      className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50">
                      {supBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} 🔄 {l.label}
                    </button>
                    <span data-testid={`nwg-lastupdate-${l.key}`} className="pl-2 text-[10px] text-zinc-500">
                      {info ? `Agg. ${fmtDate(info.date)} · ${info.rows} righe` : "Mai aggiornato"}
                    </span>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              <input ref={supUpdateRef} type="file" accept={ACCEPT} onChange={onSupplierUpdateFile} className="hidden" data-testid="supplier-update-input" />
              <button data-testid="supplier-update-btn" disabled={supBusy} onClick={() => supUpdateRef.current?.click()}
                title="Carica un nuovo file: sostituisce automaticamente il listino attivo e archivia il precedente"
                className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-500 disabled:opacity-50">
                {supBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} 🔄 Aggiorna listino
              </button>
            </>
          )}
          <button data-testid="supplier-history-btn" onClick={() => setShowHistory(true)} className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs font-semibold text-zinc-200"><ClipboardList className="h-3.5 w-3.5" /> Storico confronti</button>
          <button data-testid="add-pricelist-btn" onClick={() => setAddList(true)} className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> Nuovo listino</button>
        </div>
      </div>
      {data.price_lists.length === 0 && <p className="py-6 text-center text-sm text-zinc-500">Nessun listino. Crea la prima versione.</p>}
      <div className="space-y-2">
        {data.price_lists.map((pl) => (
          <div key={pl.id} className="space-y-1">
            <PriceListRow pl={pl} versionLabel={versionMap[pl.id]?.label} isCurrent={versionMap[pl.id]?.current}
              onChanged={(newLid) => { load(); if (newLid) setDiffForLid(newLid); }} />
            {pl.status === "ARCHIVED" && <button data-testid={`restore-pl-${pl.id}`} onClick={() => onRestore(pl)} className="ml-2 rounded-full border border-amber-500/40 px-3 py-1 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/10">↩︎ Ripristina questa versione</button>}
          </div>
        ))}
      </div>
      {edit && <SupplierModal initial={s} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
      {addList && <PriceListModal supplierId={id} onClose={() => setAddList(false)} onSaved={() => { setAddList(false); load(); }} />}
      {showHistory && <HistoryModal sid={id} onClose={() => setShowHistory(false)} />}
      {diffForLid && <DiffModal pl={{ id: diffForLid }} autoRun onClose={() => { setDiffForLid(null); load(); }} />}
    </div>
  );
}

function CompareModal({ item, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    const params = {};
    if (item.ean) params.ean = item.ean;
    if (item.sku) params.sku = item.sku;
    if (item.manufacturer_code) params.manufacturer_code = item.manufacturer_code;
    if (!item.ean && !item.sku && !item.manufacturer_code && item.model) params.model = item.model;
    compareListItems(params).then(setData).catch(() => toast.error("Confronto non riuscito")).finally(() => setBusy(false));
  }, [item]);
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div onClick={(e) => e.stopPropagation()} className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-white/10 bg-[#11141A] p-5 text-white" data-testid="compare-modal">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h4 className="flex items-center gap-1.5 font-semibold"><GitCompare className="h-4 w-4 text-[#2563EB]" /> Confronto tra fornitori</h4>
            <p className="mt-1 max-w-md truncate text-sm text-zinc-400" title={item.model}>{item.model || "—"}</p>
            <p className="text-xs text-zinc-600">{item.ean ? `EAN ${item.ean}` : item.sku ? `SKU ${item.sku}` : item.manufacturer_code ? `Cod. ${item.manufacturer_code}` : "per modello"}</p>
          </div>
          <button data-testid="compare-close" onClick={onClose} className="rounded-lg border border-white/10 p-1.5 text-zinc-400"><X className="h-4 w-4" /></button>
        </div>
        {busy ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div> : (
          data && data.results.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] border-separate border-spacing-y-1 text-xs">
                <thead><tr className="text-left uppercase tracking-wider text-zinc-500">
                  <th className="px-2">Fornitore</th><th className="px-2">Listino</th><th className="px-2">Prezzo</th><th className="px-2">Montaggio</th><th className="px-2"></th>
                </tr></thead>
                <tbody>
                  {data.results.map((r) => (
                    <tr key={r.id} data-testid={`compare-row-${r.supplier_id}`} className={r.is_cheapest ? "bg-emerald-500/10" : "bg-[#0B0E13]"}>
                      <td className="px-2 py-1.5 font-medium text-white">{r.supplier_name || "—"}</td>
                      <td className="px-2 text-zinc-500">{r.list_name || "—"}{r.list_date ? ` · ${fmtDate(r.list_date)}` : ""}</td>
                      <td className={`px-2 font-semibold ${r.is_cheapest ? "text-emerald-400" : "text-zinc-200"}`}>{fmtEur(r.supplier_cost_cents)}</td>
                      <td className="px-2 text-zinc-400">{fmtEur(r.installation_cost_cents)}</td>
                      <td className="px-2">{r.is_cheapest && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">MIGLIORE</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-xs text-zinc-500">{data.total_suppliers} fornitore/i con questo articolo.</p>
            </div>
          ) : <p className="py-6 text-center text-sm text-zinc-500">Nessun altro fornitore trovato per questo articolo.</p>
        )}
      </div>
    </div>
  );
}

function GlobalListSearch({ suppliers = [] }) {
  const [q, setQ] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [listType, setListType] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [compare, setCompare] = useState(null);
  const run = async (e) => {
    e?.preventDefault?.();
    if (q.trim().length < 2) { toast.message("Inserisci almeno 2 caratteri"); return; }
    setBusy(true);
    try {
      const params = { q: q.trim(), limit: 100 };
      if (supplierId) params.supplier_id = supplierId;
      if (listType) params.list_type = listType;
      setData(await searchListItems(params));
    }
    catch { toast.error("Ricerca non riuscita"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-[#11141A] p-4" data-testid="global-list-search">
      <h4 className="mb-2 flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-zinc-500"><Search className="h-3.5 w-3.5" /> Ricerca avanzata listini</h4>
      <form onSubmit={run} className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
        <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Modello, EAN, SKU o codice produttore…"
          className="min-w-0 flex-1 rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-sm text-white placeholder:text-zinc-600" />
        <select data-testid="global-search-supplier" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}
          className="rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-sm text-white">
          <option value="">Tutti i fornitori</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select data-testid="global-search-type" value={listType} onChange={(e) => setListType(e.target.value)}
          className="rounded-lg border border-white/10 bg-[#0B0E13] px-3 py-2 text-sm text-white">
          <option value="">Tutti i tipi</option>
          {LIST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button data-testid="global-search-btn" type="submit" disabled={busy} className="flex items-center justify-center gap-1.5 rounded-lg bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Cerca
        </button>
      </form>
      {data && (
        <div className="mt-3">
          <p className="mb-2 text-xs text-zinc-500">{data.total} risultati per "{data.query}"{data.total > data.results.length ? ` (primi ${data.results.length})` : ""}</p>
          {data.results.length > 0 && (
            <div className="max-h-72 overflow-auto">
              <table className="w-full min-w-[780px] border-separate border-spacing-y-1 text-xs">
                <thead><tr className="text-left uppercase tracking-wider text-zinc-500">
                  <th className="px-2">Fornitore</th><th className="px-2">Listino</th><th className="px-2">Modello</th>
                  <th className="px-2">SKU</th><th className="px-2">EAN</th><th className="px-2">Prezzo</th><th className="px-2">Montaggio</th><th className="px-2"></th>
                </tr></thead>
                <tbody>
                  {data.results.map((r) => (
                    <tr key={r.id} data-testid={`global-result-${r.id}`} className="bg-[#0B0E13]">
                      <td className="px-2 py-1 text-zinc-300">{r.supplier_name || "—"}</td>
                      <td className="px-2 text-zinc-500">{r.list_name || "—"}{r.list_date ? ` · ${fmtDate(r.list_date)}` : ""}</td>
                      <td className="max-w-[220px] truncate px-2 text-white" title={r.model}>{r.model || "—"}</td>
                      <td className="px-2 text-zinc-400">{r.sku || "—"}</td>
                      <td className="px-2 text-zinc-400">{r.ean || "—"}</td>
                      <td className="px-2 text-zinc-200">{fmtEur(r.supplier_cost_cents)}</td>
                      <td className="px-2 text-zinc-200">{fmtEur(r.installation_cost_cents)}</td>
                      <td className="px-2">
                        <button data-testid={`compare-btn-${r.id}`} onClick={() => setCompare(r)} title="Confronta tra fornitori"
                          className="flex items-center gap-1 rounded-lg border border-white/10 px-2 py-1 text-[10px] font-semibold text-zinc-200 hover:border-[#2563EB]">
                          <GitCompare className="h-3 w-3" /> Confronta
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {compare && <CompareModal item={compare} onClose={() => setCompare(null)} />}
    </div>
  );
}

export default function Suppliers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(null);
  const load = () => listSuppliers().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const toggle = async (s) => { await updateSupplier(s.id, { active: !s.active }); toast.success(s.active ? "Disattivato" : "Attivato"); load(); };

  if (sel) return <SupplierDetail id={sel} onBack={() => { setSel(null); load(); }} />;

  return (
    <div className="space-y-4" data-testid="suppliers-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">📦 Listini Fornitori</h3>
          <p className="mt-1 text-sm text-zinc-400">Anagrafica fornitori, storico versioni e caricamento/analisi listini (PDF/XLS/XLSX/CSV).</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="backfill-legacy-btn"
            onClick={async () => { try { const r = await legacyBackfill(); toast.success(`Backfill storico: ${r.totals.inserted} righe inserite, ${r.totals.skipped_dup} duplicati saltati (${r.totals.lists} listini)`); load(); } catch (e) { toast.error(errMsg(e) || "Backfill non riuscito"); } }}
            className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 px-4 py-2 text-sm font-semibold text-emerald-300" title="Backfill una-tantum dei listini storici nello storico analizzato (idempotente)"><Database className="h-4 w-4" /> Backfill storico</button>
          <button data-testid="add-supplier-btn" onClick={() => setOpen(true)} className="flex items-center gap-1.5 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Aggiungi fornitore</button>
        </div>
      </div>

      <GlobalListSearch suppliers={rows} />

      {rows.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessun fornitore. Aggiungine uno per iniziare.</p>}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-separate border-spacing-y-2 text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500">
              <th className="px-3">Fornitore</th><th className="px-3">Tipo</th><th className="px-3">Ultimo listino</th>
              <th className="px-3">Articoli</th><th className="px-3">Versioni</th><th className="px-3">Stato</th><th className="px-3">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} data-testid={`supplier-row-${s.id}`} className="bg-[#11141A]">
                <td className="rounded-l-lg px-3 py-2 font-medium text-white">{s.name}{s.category && <span className="ml-2 text-xs text-zinc-500">{s.category}</span>}</td>
                <td className="px-3 py-2 text-zinc-400">{s.source_type || "—"}</td>
                <td className="px-3 py-2 text-zinc-400">{fmtDate(s.last_list_date)}</td>
                <td className="px-3 py-2 text-zinc-300">{s.articles ?? 0}</td>
                <td className="px-3 py-2 text-zinc-300">{s.price_lists_count ?? 0}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] ${s.active ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-500/15 text-zinc-400"}`}>{s.active ? "ATTIVO" : "OFF"}</span></td>
                <td className="rounded-r-lg px-3 py-2">
                  <div className="flex gap-1.5">
                    <button data-testid={`supplier-open-${s.id}`} onClick={() => setSel(s.id)} className="rounded-lg border border-white/10 px-2.5 py-1 text-xs text-zinc-200">Apri</button>
                    <button data-testid={`supplier-toggle-${s.id}`} onClick={() => toggle(s)} className="rounded-lg border border-white/10 px-2 py-1 text-zinc-400" title={s.active ? "Disattiva" : "Attiva"}><Power className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <SupplierModal onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}
